import React, { useState } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import {
  Package, Plus, Search, Trash2, Edit2, Loader2, Check, AlertTriangle,
  ArrowUpCircle, ArrowDownCircle, History, DollarSign, BarChart3, ShoppingCart,
  Clock, FileText, Award
} from "lucide-react";
import { differenceInDays, parseISO } from "date-fns";
import EstoqueValorizacao from "../components/estoque/EstoqueValorizacao";
import AlertasValidade from "../components/estoque/AlertasValidade";
import CurvaABC from "../components/estoque/CurvaABC";
import EntradaNotaFiscal from "../components/estoque/EntradaNotaFiscal";
import ImportacaoXML from "../components/estoque/ImportacaoXML";
import MovimentacaoHistorico from "../components/estoque/MovimentacaoHistorico";
import HistoricoNotasFiscais from "../components/estoque/HistoricoNotasFiscais";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const categorias = [
  { value: "semente", label: "Sementes" },
  { value: "fertilizante", label: "Fertilizantes" },
  { value: "defensivo", label: "Defensivos" },
  { value: "combustivel", label: "Combustível" },
  { value: "racao", label: "Ração" },
  { value: "medicamento", label: "Medicamentos" },
  { value: "outro", label: "Outros" }
];

export default function Estoque() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const urlParams = new URLSearchParams(window.location.search);
  const novoInsumo = urlParams.get('novo') === 'true';

  const [dialogOpen, setDialogOpen] = useState(novoInsumo);
  const [movDialogOpen, setMovDialogOpen] = useState(false);
  const [editingInsumo, setEditingInsumo] = useState(null);
  const [selectedInsumo, setSelectedInsumo] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("todas");

  const [formData, setFormData] = useState({
    nome: "", categoria: "semente", unidade: "", quantidade_estoque: "",
    quantidade_minima: "", custo_unitario: "", fornecedor: "", lote: "",
    validade: "", nota_fiscal: ""
  });

  const [movData, setMovData] = useState({
    tipo: "saida", quantidade: "", observacao: "", talhao_id: "", safra_id: "",
    motivo: "consumo", preco_venda: ""
  });
  const [activeTab, setActiveTab] = useState("itens");
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedInsumoHistory, setSelectedInsumoHistory] = useState(null);

  const queryClient = useQueryClient();

  const { data: insumos = [], isLoading } = useQuery({
    queryKey: ['insumos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Insumo.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: movimentacoes = [] } = useQuery({
    queryKey: ['movimentacoes', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.MovimentacaoInsumo.filter({ propriedade_id: propriedadeId }, '-data', 100)
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: talhoes = [] } = useQuery({
    queryKey: ['talhoes', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Talhao.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: safras = [] } = useQuery({
    queryKey: ['safras', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Safra.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: unidades = [] } = useQuery({
    queryKey: ['unidades_medida'],
    queryFn: () => agrogestao.entities.UnidadeMedida.filter({}, 'sigla')
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const u = await agrogestao.auth.me();
      const payload = {
        propriedade_id: propriedadeId,
        nome: data.nome,
        categoria: data.categoria,
        unidade: data.unidade,
        quantidade_estoque: typeof data.quantidade_estoque === 'number' ? data.quantidade_estoque : 0,
        quantidade_minima: typeof data.quantidade_minima === 'number' ? data.quantidade_minima : null,
        custo_unitario: typeof data.custo_unitario === 'number' ? data.custo_unitario : 0,
        fornecedor: data.fornecedor || null,
        lote: data.lote || null,
        validade: data.validade || null,
        nota_fiscal: data.nota_fiscal || null,
        created_by: u?.email || null
      };
      return agrogestao.entities.Insumo.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insumos'] });
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => {
      const payload = {
        nome: data.nome,
        categoria: data.categoria,
        unidade: data.unidade,
        quantidade_estoque: typeof data.quantidade_estoque === 'number' ? data.quantidade_estoque : 0,
        quantidade_minima: typeof data.quantidade_minima === 'number' ? data.quantidade_minima : null,
        custo_unitario: typeof data.custo_unitario === 'number' ? data.custo_unitario : 0,
        fornecedor: data.fornecedor || null,
        lote: data.lote || null,
        validade: data.validade || null,
        nota_fiscal: data.nota_fiscal || null
      };
      return agrogestao.entities.Insumo.update(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insumos'] });
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.Insumo.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insumos'] })
  });

  const movMutation = useMutation({
    mutationFn: async (data) => {
      const custoTotal = data.quantidade * (selectedInsumo.custo_unitario || 0);
      const dataHoje = new Date().toISOString().split('T')[0];

      // Criar movimentação
      const { motivo, preco_venda, ...cleanData } = data;
      await agrogestao.entities.MovimentacaoInsumo.create({
        ...cleanData,
        propriedade_id: propriedadeId,
        insumo_id: selectedInsumo.id,
        data: dataHoje,
        custo_total: custoTotal,
        talhao_id: data.talhao_id || null,
        safra_id: data.safra_id || null
      });

      // Atualizar estoque
      const newQtd = data.tipo === 'entrada'
        ? selectedInsumo.quantidade_estoque + data.quantidade
        : selectedInsumo.quantidade_estoque - data.quantidade;

      await agrogestao.entities.Insumo.update(selectedInsumo.id, { quantidade_estoque: newQtd });

      // Integração Financeira
      if (data.tipo === 'saida') {
        if (data.motivo === 'venda' && data.preco_venda) {
          // Criar Conta a Receber para venda
          await agrogestao.entities.ContaReceber.create({
            propriedade_id: propriedadeId,
            descricao: `Venda: ${selectedInsumo.nome} (${data.quantidade} ${selectedInsumo.unidade})`,
            valor: parseFloat(data.preco_venda),
            data_vencimento: dataHoje,
            categoria: "venda_hortifruti",
            status: "pendente",
            observacoes: data.observacao
          });
        } else if (data.motivo === 'consumo') {
          // Criar Custo Alocado para consumo interno
          await agrogestao.entities.CustoAlocado.create({
            propriedade_id: propriedadeId,
            descricao: `Consumo: ${selectedInsumo.nome} (${data.quantidade} ${selectedInsumo.unidade})`,
            valor: custoTotal,
            data: dataHoje,
            tipo_custo: "variavel",
            categoria: "insumos",
            safra_id: data.safra_id || null,
            talhao_id: data.talhao_id || null,
            unidade_medida: selectedInsumo.unidade,
            quantidade_unidades: data.quantidade,
            custo_unitario: selectedInsumo.custo_unitario,
            observacoes: data.observacao
          });
        }
      } else if (data.tipo === 'entrada') {
        // Criar Conta a Pagar para compra/entrada
        await agrogestao.entities.ContaPagar.create({
          propriedade_id: propriedadeId,
          descricao: `Compra: ${selectedInsumo.nome} (${data.quantidade} ${selectedInsumo.unidade})`,
          valor: custoTotal,
          data_vencimento: dataHoje,
          categoria: "insumos",
          fornecedor: selectedInsumo.fornecedor || "",
          status: "pendente",
          observacoes: data.observacao
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insumos'] });
      queryClient.invalidateQueries({ queryKey: ['movimentacoes'] });
      queryClient.invalidateQueries({ queryKey: ['contas_pagar'] });
      queryClient.invalidateQueries({ queryKey: ['contas_receber'] });
      queryClient.invalidateQueries({ queryKey: ['custos_alocados'] });
      setMovDialogOpen(false);
      setMovData({ tipo: "saida", quantidade: "", observacao: "", talhao_id: "", safra_id: "", motivo: "consumo", preco_venda: "" });
    }
  });

  const resetForm = () => {
    setFormData({
      nome: "", categoria: "semente", unidade: "", quantidade_estoque: "",
      quantidade_minima: "", custo_unitario: "", fornecedor: "", lote: "",
      validade: "", nota_fiscal: ""
    });
    setEditingInsumo(null);
    setDialogOpen(false);
  };

  const handleEdit = (insumo) => {
    setFormData({
      nome: insumo.nome || "",
      categoria: insumo.categoria || "semente",
      unidade: insumo.unidade || "",
      quantidade_estoque: insumo.quantidade_estoque?.toString() || "",
      quantidade_minima: insumo.quantidade_minima?.toString() || "",
      custo_unitario: insumo.custo_unitario?.toString() || "",
      fornecedor: insumo.fornecedor || "",
      lote: insumo.lote || "",
      validade: insumo.validade || "",
      nota_fiscal: insumo.nota_fiscal || ""
    });
    setEditingInsumo(insumo);
    setDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      quantidade_estoque: parseFloat(formData.quantidade_estoque) || 0,
      quantidade_minima: formData.quantidade_minima ? parseFloat(formData.quantidade_minima) : null,
      custo_unitario: formData.custo_unitario ? parseFloat(formData.custo_unitario) : null
    };

    if (editingInsumo) {
      updateMutation.mutate({ id: editingInsumo.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleMov = (e) => {
    e.preventDefault();
    movMutation.mutate({
      ...movData,
      quantidade: parseFloat(movData.quantidade)
    });
  };

  const openMovDialog = (insumo) => {
    setSelectedInsumo(insumo);
    setMovDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const filteredInsumos = insumos.filter(i => {
    const matchSearch = !searchTerm ||
      i.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.fornecedor?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategoria = filterCategoria === "todas" || i.categoria === filterCategoria;
    return matchSearch && matchCategoria;
  });

  const insumosBaixos = insumos.filter(i =>
    i.quantidade_minima && i.quantidade_estoque <= i.quantidade_minima
  );

  const valorTotalEstoque = insumos.reduce((acc, i) =>
    acc + (i.quantidade_estoque || 0) * (i.custo_unitario || 0), 0
  );

  // Itens próximos do vencimento (90 dias)
  const hoje = new Date();
  const insumosVencimento = insumos.filter(i => {
    if (!i.validade) return false;
    const diasRestantes = differenceInDays(parseISO(i.validade), hoje);
    return diasRestantes <= 90;
  });

  if (!propriedadeId) {
    return (
      <div className="text-center py-20">
        <p className="text-stone-600">Selecione uma propriedade para gerenciar o estoque</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#40916C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800 flex items-center gap-3">
            <Package className="w-8 h-8 text-blue-600" />
            Estoque de Insumos
          </h1>
          <p className="text-stone-600 mt-1">Controle de insumos e materiais</p>
        </div>

        <div className="flex gap-3">
          <ImportacaoXML insumos={insumos} propriedadeId={propriedadeId} />
          <EntradaNotaFiscal insumos={insumos} propriedadeId={propriedadeId} />

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetForm}
                className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2"
              >
                <Plus className="w-5 h-5" />
                Novo Insumo
              </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-600" />
                  {editingInsumo ? "Editar Insumo" : "Novo Insumo"}
                </DialogTitle>
                <DialogDescription>
                  {editingInsumo ? "Atualize os dados do insumo" : "Cadastre um novo insumo"}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Nome do Insumo *</Label>
                  <Input
                    placeholder="Ex: Semente Milho BG"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Categoria *</Label>
                    <Select
                      value={formData.categoria}
                      onValueChange={(v) => setFormData({ ...formData, categoria: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categorias.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Unidade *</Label>
                    <Select
                      value={formData.unidade}
                      onValueChange={(v) => setFormData({ ...formData, unidade: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {unidades.map(u => (
                          <SelectItem key={u.id} value={u.sigla}>{u.sigla} - {u.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Quantidade em Estoque *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={formData.quantidade_estoque}
                      onChange={(e) => setFormData({ ...formData, quantidade_estoque: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Qtd. Mínima (Alerta)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={formData.quantidade_minima}
                      onChange={(e) => setFormData({ ...formData, quantidade_minima: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Custo Unitário (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0,00"
                      value={formData.custo_unitario}
                      onChange={(e) => setFormData({ ...formData, custo_unitario: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Fornecedor</Label>
                    <Input
                      placeholder="Nome do fornecedor"
                      value={formData.fornecedor}
                      onChange={(e) => setFormData({ ...formData, fornecedor: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Lote</Label>
                    <Input
                      placeholder="Número do lote"
                      value={formData.lote}
                      onChange={(e) => setFormData({ ...formData, lote: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Validade</Label>
                    <Input
                      type="date"
                      value={formData.validade}
                      onChange={(e) => setFormData({ ...formData, validade: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]"
                  >
                    {(createMutation.isPending || updateMutation.isPending) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <><Check className="w-4 h-4 mr-2" /> {editingInsumo ? "Atualizar" : "Cadastrar"}</>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total de Itens</p>
              <p className="text-2xl font-bold mt-1">{insumos.length}</p>
            </div>
            <Package className="w-10 h-10 text-blue-200" />
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-[#2D6A4F] to-[#1B4332] text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">Valor em Estoque</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(valorTotalEstoque)}</p>
            </div>
            <DollarSign className="w-10 h-10 text-white/50" />
          </div>
        </Card>

        <Card className={`p-5 ${insumosBaixos.length > 0 ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-gradient-to-br from-green-500 to-green-600'} text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Estoque Baixo</p>
              <p className="text-2xl font-bold mt-1">{insumosBaixos.length} itens</p>
            </div>
            <AlertTriangle className="w-10 h-10 text-white/50" />
          </div>
        </Card>

        <Card className={`p-5 ${insumosVencimento.length > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-purple-500 to-purple-600'} text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Vencimento Próximo</p>
              <p className="text-2xl font-bold mt-1">{insumosVencimento.length} itens</p>
            </div>
            <Clock className="w-10 h-10 text-white/50" />
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100 p-1 flex-wrap h-auto">
          <TabsTrigger value="itens" className="gap-2">
            <Package className="w-4 h-4" />
            Itens
          </TabsTrigger>
          <TabsTrigger value="valorizacao" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Valorização
          </TabsTrigger>
          <TabsTrigger value="validade" className="gap-2">
            <Clock className="w-4 h-4" />
            Validade
          </TabsTrigger>
          <TabsTrigger value="curva_abc" className="gap-2">
            <Award className="w-4 h-4" />
            Curva ABC
          </TabsTrigger>
          <TabsTrigger value="movimentacao" className="gap-2">
            <History className="w-4 h-4" />
            Movimentação
          </TabsTrigger>
          <TabsTrigger value="notas_fiscais" className="gap-2">
            <FileText className="w-4 h-4" />
            Notas Fiscais
          </TabsTrigger>
        </TabsList>

        <TabsContent value="itens" className="mt-6 space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-2xl border border-stone-200 p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  placeholder="Buscar insumo ou fornecedor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  {categorias.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Insumos Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filteredInsumos.map((insumo, index) => {
                const isLow = insumo.quantidade_minima && insumo.quantidade_estoque <= insumo.quantidade_minima;
                const percentEstoque = insumo.quantidade_minima
                  ? Math.min((insumo.quantidade_estoque / (insumo.quantidade_minima * 2)) * 100, 100)
                  : 50;

                return (
                  <motion.div
                    key={insumo.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card
                      className={`bg-white border-stone-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer group ${isLow ? 'ring-2 ring-amber-400' : ''}`}
                      onClick={() => {
                        setSelectedInsumoHistory(insumo);
                        setHistoryDialogOpen(true);
                      }}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-bold text-stone-800 group-hover:text-blue-600 transition-colors">{insumo.nome}</h3>
                            <Badge className="mt-1" variant="secondary">
                              {categorias.find(c => c.value === insumo.categoria)?.label}
                            </Badge>
                          </div>
                          {isLow && <AlertTriangle className="w-5 h-5 text-amber-500" />}
                        </div>

                        <div className="space-y-3">
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-stone-500">Estoque</span>
                              <span className="font-semibold">
                                {insumo.quantidade_estoque} {insumo.unidade}
                              </span>
                            </div>
                            <Progress
                              value={percentEstoque}
                              className={`h-2 ${isLow ? '[&>div]:bg-amber-500' : '[&>div]:bg-green-500'}`}
                            />
                            {insumo.quantidade_minima && (
                              <p className="text-xs text-stone-400 mt-1">
                                Mínimo: {insumo.quantidade_minima} {insumo.unidade}
                              </p>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <p className="text-stone-500">Custo Unit.</p>
                              <p className="font-medium">{formatCurrency(insumo.custo_unitario)}</p>
                            </div>
                            <div>
                              <p className="text-stone-500">Valor Total</p>
                              <p className="font-medium">
                                {formatCurrency((insumo.quantidade_estoque || 0) * (insumo.custo_unitario || 0))}
                              </p>
                            </div>
                          </div>

                          {insumo.fornecedor && (
                            <p className="text-xs text-stone-500">
                              Fornecedor: {insumo.fornecedor}
                            </p>
                          )}

                          {/* Lote e Validade */}
                          {(insumo.lote || insumo.validade) && (
                            <div className="flex flex-wrap gap-2 text-xs">
                              {insumo.lote && (
                                <Badge variant="outline" className="text-xs">
                                  Lote: {insumo.lote}
                                </Badge>
                              )}
                              {insumo.validade && (() => {
                                const diasRestantes = differenceInDays(parseISO(insumo.validade), hoje);
                                let badgeClass = "bg-green-100 text-green-700";
                                if (diasRestantes < 0) badgeClass = "bg-red-100 text-red-700";
                                else if (diasRestantes <= 30) badgeClass = "bg-amber-100 text-amber-700";
                                else if (diasRestantes <= 90) badgeClass = "bg-yellow-100 text-yellow-700";
                                return (
                                  <Badge className={badgeClass}>
                                    {diasRestantes < 0 ? `Vencido há ${Math.abs(diasRestantes)}d` : `Vence em ${diasRestantes}d`}
                                  </Badge>
                                );
                              })()}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2 mt-4 pt-4 border-t border-stone-100">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              openMovDialog(insumo);
                            }}
                          >
                            <History className="w-4 h-4 mr-1" />
                            Movimentar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(insumo);
                            }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm("Deseja excluir este insumo?")) {
                                deleteMutation.mutate(insumo.id);
                              }
                            }}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {filteredInsumos.length === 0 && (
            <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
              <Package className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">Nenhum insumo encontrado</p>
            </div>
          )}
        </TabsContent>

        {/* Valorização */}
        <TabsContent value="valorizacao" className="mt-6">
          <EstoqueValorizacao insumos={insumos} />
        </TabsContent>

        {/* Validade */}
        <TabsContent value="validade" className="mt-6">
          <AlertasValidade insumos={insumos} onEdit={handleEdit} />
        </TabsContent>

        {/* Curva ABC */}
        <TabsContent value="curva_abc" className="mt-6">
          <CurvaABC insumos={insumos} />
        </TabsContent>

        {/* Movimentação */}
        <TabsContent value="movimentacao" className="mt-6">
          <MovimentacaoHistorico movimentacoes={movimentacoes} insumos={insumos} />
        </TabsContent>

        {/* Notas Fiscais */}
        <TabsContent value="notas_fiscais" className="mt-6">
          <HistoricoNotasFiscais insumos={insumos} movimentacoes={movimentacoes} />
        </TabsContent>
      </Tabs>

      {/* Movement Dialog */}
      <Dialog open={movDialogOpen} onOpenChange={setMovDialogOpen}>
        <DialogContent className="w-[95%] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Movimentar {selectedInsumo?.nome}</DialogTitle>
            <DialogDescription>
              Registre entrada ou saída de insumos
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleMov} className="space-y-4 pt-4">
            <div className="flex flex-col sm:flex-row gap-2 p-1 bg-stone-100 rounded-xl">
              <button
                type="button"
                onClick={() => setMovData(p => ({ ...p, tipo: 'entrada' }))}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${movData.tipo === 'entrada' ? 'bg-green-500 text-white shadow' : 'text-stone-600'
                  }`}
              >
                <ArrowUpCircle className="w-4 h-4" /> Entrada
              </button>
              <button
                type="button"
                onClick={() => setMovData(p => ({ ...p, tipo: 'saida' }))}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${movData.tipo === 'saida' ? 'bg-red-500 text-white shadow' : 'text-stone-600'
                  }`}
              >
                <ArrowDownCircle className="w-4 h-4" /> Saída
              </button>
            </div>

            <div className="space-y-2">
              <Label>Quantidade *</Label>
              <Input
                type="number"
                step="0.01"
                placeholder={`Em ${selectedInsumo?.unidade}`}
                value={movData.quantidade}
                onChange={(e) => setMovData({ ...movData, quantidade: e.target.value })}
                required
                className="h-11 sm:h-auto"
              />
              <p className="text-xs text-stone-500">
                Estoque atual: {selectedInsumo?.quantidade_estoque} {selectedInsumo?.unidade}
              </p>
            </div>

            {movData.tipo === 'saida' && (
              <div className="space-y-4">
                {/* Motivo da saída */}
                <div className="space-y-2">
                  <Label>Motivo da Saída *</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => setMovData(p => ({ ...p, motivo: 'consumo', preco_venda: '' }))}
                      className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 border ${movData.motivo === 'consumo' ? 'bg-orange-100 border-orange-400 text-orange-700' : 'border-stone-200 text-stone-600'
                        }`}
                    >
                      <Package className="w-4 h-4" /> Consumo
                    </button>
                    <button
                      type="button"
                      onClick={() => setMovData(p => ({ ...p, motivo: 'venda' }))}
                      className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 border ${movData.motivo === 'venda' ? 'bg-green-100 border-green-400 text-green-700' : 'border-stone-200 text-stone-600'
                        }`}
                    >
                      <ShoppingCart className="w-4 h-4" /> Venda
                    </button>
                  </div>
                </div>

                {movData.motivo === 'venda' && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200 space-y-2">
                    <Label className="text-green-700">Valor da Venda (R$) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0,00"
                      value={movData.preco_venda}
                      onChange={(e) => setMovData({ ...movData, preco_venda: e.target.value })}
                      className="bg-white"
                      required={movData.motivo === 'venda'}
                    />
                    <p className="text-xs text-green-600">
                      Será criada uma Conta a Receber automaticamente
                    </p>
                  </div>
                )}

                {movData.motivo === 'consumo' && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-xs text-orange-600">
                      <DollarSign className="w-3 h-3 inline mr-1" />
                      Custo de {formatCurrency((parseFloat(movData.quantidade) || 0) * (selectedInsumo?.custo_unitario || 0))} será alocado
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Talhão</Label>
                    <Select
                      value={movData.talhao_id}
                      onValueChange={(v) => setMovData({ ...movData, talhao_id: v })}
                    >
                      <SelectTrigger className="h-11 sm:h-auto">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={null}>Nenhum</SelectItem>
                        {talhoes.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Safra</Label>
                    <Select
                      value={movData.safra_id}
                      onValueChange={(v) => setMovData({ ...movData, safra_id: v })}
                    >
                      <SelectTrigger className="h-11 sm:h-auto">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={null}>Nenhuma</SelectItem>
                        {safras.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {movData.tipo === 'entrada' && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-600">
                  <DollarSign className="w-3 h-3 inline mr-1" />
                  Será criada uma Conta a Pagar de {formatCurrency((parseFloat(movData.quantidade) || 0) * (selectedInsumo?.custo_unitario || 0))}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Observação</Label>
              <Input
                placeholder="Motivo da movimentação"
                value={movData.observacao}
                onChange={(e) => setMovData({ ...movData, observacao: e.target.value })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setMovDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={movMutation.isPending}
                className={`flex-1 ${movData.tipo === 'entrada' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'}`}
              >
                {movMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <><Check className="w-4 h-4 mr-2" /> Confirmar</>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="flex items-center gap-3 text-2xl">
              <History className="w-6 h-6 text-blue-600" />
              Histórico: {selectedInsumoHistory?.nome}
            </DialogTitle>
            <DialogDescription>
              Rastreabilidade completa de entradas e saídas
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 pb-6 mt-4">
            <div className="bg-stone-50 rounded-lg p-4 mb-6 grid grid-cols-3 gap-4 border border-stone-200">
              <div className="text-center border-r border-stone-200">
                <p className="text-xs text-stone-500 uppercase font-bold">Estoque Atual</p>
                <p className="text-xl font-bold text-stone-800">
                  {selectedInsumoHistory?.quantidade_estoque} {selectedInsumoHistory?.unidade}
                </p>
              </div>
              <div className="text-center border-r border-stone-200">
                <p className="text-xs text-stone-500 uppercase font-bold">Total Entradas</p>
                <p className="text-xl font-bold text-green-600">
                  {movimentacoes
                    .filter(m => m.insumo_id === selectedInsumoHistory?.id && m.tipo === 'entrada')
                    .reduce((a, b) => a + (b.quantidade || 0), 0).toFixed(2)} {selectedInsumoHistory?.unidade}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-stone-500 uppercase font-bold">Total Saídas</p>
                <p className="text-xl font-bold text-red-600">
                  {movimentacoes
                    .filter(m => m.insumo_id === selectedInsumoHistory?.id && m.tipo === 'saida')
                    .reduce((a, b) => a + (b.quantidade || 0), 0).toFixed(2)} {selectedInsumoHistory?.unidade}
                </p>
              </div>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead className="w-[100px]">Data</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Qtd</TableHead>
                    <TableHead>Destino/Observação</TableHead>
                    <TableHead className="text-right">Custo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movimentacoes
                    .filter(m => m.insumo_id === selectedInsumoHistory?.id)
                    .sort((a, b) => new Date(b.data) - new Date(a.data))
                    .map((m) => (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium">
                          {format(parseISO(m.data), "dd/MM/yy")}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline"
                            className={m.tipo === 'entrada' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}>
                            {m.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                          </Badge>
                        </TableCell>
                        <TableCell className={`text-right font-bold ${m.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'}`}>
                          {m.tipo === 'entrada' ? '+' : '-'}{m.quantidade} {selectedInsumoHistory?.unidade}
                        </TableCell>
                        <TableCell className="text-xs text-stone-500 max-w-[200px] truncate">
                          {m.observacao || "-"}
                        </TableCell>
                        <TableCell className="text-right text-stone-600">
                          {formatCurrency(m.custo_total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  {movimentacoes.filter(m => m.insumo_id === selectedInsumoHistory?.id).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-stone-400">
                        Nenhuma movimentação registrada para este item.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
